import Cookies from "js-cookie";
import { STORAGE_KEYS, STORAGE_DURATIONS } from "./storage";
import {
  SESSION_ID_COOKIE,
  SessionRecord,
  parseSessionRecord,
} from "./sessionCookie";

const SESSION_ID_KEY = SESSION_ID_COOKIE;
const SESSION_ID_TTL = STORAGE_DURATIONS.MINUTES_30;

export const STORAGE_KEYS_EXTENDED = {
  ...STORAGE_KEYS,
  CART_SESSION_ID: SESSION_ID_KEY,
} as const;

const readRecord = (): SessionRecord | null => {
  const record = parseSessionRecord(Cookies.get(SESSION_ID_KEY));
  if (!record) return null;
  // Cookie expiry TTL'i zaten uyguluyor; bu kontrol saat kayması / elle
  // düzenlenmiş cookie durumlarına karşı ikinci bir savunma.
  if (Date.now() - record.timestamp > SESSION_ID_TTL) return null;
  return record;
};

/**
 * Session Storage Management
 * Handles guest and authenticated user sessions
 *
 * NOT: cookie'de tutulur, localStorage'da değil. Sebep: sunucu bileşenlerinin
 * (home section fetch'leri) misafir kullanıcı için `X-Session-Id` gönderebilmesi
 * gerekiyor ve sunucunun localStorage'a erişimi yok. Cookie httpOnly DEĞİL —
 * axiosInstance client'ta da aynı değeri okuyup header'a koyuyor.
 */
export const sessionStorage = {
  /**
   * Get existing session ID or create a new one
   * This ensures every user (guest or authenticated) has a session ID
   */
  getSessionId: (): string => {
    if (typeof document === "undefined") return "";

    const record = readRecord();
    if (record) return record.value;

    const sessionId = sessionStorage.generateSessionId();
    sessionStorage.setSessionId(sessionId);
    return sessionId;
  },

  /**
   * Generate a new unique session ID using UUID v4
   */
  generateSessionId: (): string => {
    return crypto.randomUUID();
  },

  /**
   * Set session ID in storage
   * Expires after SESSION_ID_TTL — getSessionId() then generates a fresh one
   */
  setSessionId: (id: string): void => {
    if (typeof document === "undefined") return;

    const record: SessionRecord = { value: id, timestamp: Date.now() };
    Cookies.set(SESSION_ID_KEY, JSON.stringify(record), {
      expires: new Date(Date.now() + SESSION_ID_TTL),
      sameSite: "lax",
      secure: process.env.NODE_ENV === "production",
      path: "/",
    });
  },

  /**
   * Clear session ID (used on logout)
   */
  clearSessionId: (): void => {
    Cookies.remove(SESSION_ID_KEY, { path: "/" });
  },

  /**
   * Check if session ID exists
   */
  hasSessionId: (): boolean => {
    return readRecord() !== null;
  },

  /**
   * Refresh session ID (generate new one)
   * Used when switching between guest and authenticated sessions
   */
  refreshSessionId: (): string => {
    const newSessionId = sessionStorage.generateSessionId();
    sessionStorage.setSessionId(newSessionId);
    return newSessionId;
  },

  /**
   * Mevcut session_id'nin SESSION_ID_TTL dolana kadar kalan süresi (ms).
   * Kayıt yoksa/okunamıyorsa 0 döner.
   */
  getRemainingTtl: (): number => {
    const record = readRecord();
    if (record === null) return 0;

    return Math.max(0, SESSION_ID_TTL - (Date.now() - record.timestamp));
  },
};

